import { NewListingBadge } from "@/components/property/new-listing-badge";
import { Link } from "wouter";
import { Check, Minus } from "lucide-react";
import type { PublicProperty } from "@shared/schema";
import { formatTimeToStandard } from "@/lib/time-utils";
import { SectionHeading } from "@/components/ui/section-heading";
import { RateLine, rateParts } from "@/components/property/rate-line";

type Home = PublicProperty & { booking_enabled?: boolean };

const img = (src: string, w: number) => `/img?src=${encodeURIComponent(src)}&w=${w}&q=90`;

function coverOf(home: Home): string | undefined {
  const first = [...(home.photos || [])].sort((a, b) => a.sort_order - b.sort_order)[0];
  return home.hero_image_url || first?.url || undefined;
}

function hasAmenity(home: Home, pattern: RegExp): boolean {
  return (home.amenities || []).some((a) => pattern.test(a.name));
}

function YesNo({ yes }: { yes: boolean }) {
  return yes ? (
    <Check className="mx-auto h-5 w-5 text-palm" aria-label="Yes" strokeWidth={2} />
  ) : (
    <Minus className="mx-auto h-4 w-4 text-driftwood" aria-label="No" />
  );
}

interface Row {
  label: string;
  cell: (home: Home) => React.ReactNode;
}

/** Facts first, then the features that differ or that guests ask about. */
const ROWS: Row[] = [
  { label: "Sleeps", cell: (h) => <span className="font-serif text-base md:text-lg text-ink">{h.max_guests} guests</span> },
  { label: "Bedrooms", cell: (h) => <span className="font-serif text-lg text-ink">{h.bedrooms}</span> },
  { label: "Bathrooms", cell: (h) => <span className="font-serif text-lg text-ink">{h.bathrooms}</span> },
  { label: "Nightly rate", cell: (h) => rateParts(h).rate ? <RateLine home={h} size="sm" className="inline" /> : <Minus className="mx-auto h-4 w-4 text-driftwood" aria-label="Not listed" /> },
  {
    label: "Check-in / checkout",
    cell: (h) => (
      <span className="font-sans text-ink-soft">
        {formatTimeToStandard(h.check_in_time) || "3:00 PM"} / {formatTimeToStandard(h.check_out_time) || "10:00 AM"}
      </span>
    ),
  },
  { label: "Beachfront", cell: (h) => <YesNo yes={hasAmenity(h, /beachfront|oceanfront/i)} /> },
  { label: "Ocean view", cell: (h) => <YesNo yes={hasAmenity(h, /ocean view/i)} /> },
  { label: "Mountain view", cell: (h) => <YesNo yes={hasAmenity(h, /mountain view/i)} /> },
  { label: "Kayaks and snorkel gear", cell: (h) => <YesNo yes={hasAmenity(h, /kayak/i) && hasAmenity(h, /snorkel/i)} /> },
  { label: "Full kitchen", cell: (h) => <YesNo yes={hasAmenity(h, /full kitchen/i)} /> },
  { label: "Washer in the home", cell: (h) => <YesNo yes={hasAmenity(h, /washer/i)} /> },
  { label: "Air conditioning", cell: (h) => <YesNo yes={hasAmenity(h, /\bAC\b|air conditioning/i)} /> },
  { label: "Crib and high chair", cell: (h) => <YesNo yes={hasAmenity(h, /crib/i) && hasAmenity(h, /high chair/i)} /> },
  { label: "Single level, no stairs", cell: (h) => <YesNo yes={hasAmenity(h, /single-level|no stairs/i)} /> },
  { label: "Parties and events", cell: () => <span className="font-sans text-ink-soft">Not permitted</span> },
];

interface HomeComparisonProps {
  homes: Home[];
  className?: string;
}

/**
 * The homes side by side: one column per home, one row per fact, so a guest
 * choosing between them sees the differences in one place instead of reading
 * two listings. Renders nothing with fewer than two homes.
 */
export function HomeComparison({ homes, className = "" }: HomeComparisonProps) {
  if (homes.length < 2) return null;
  const cols = homes.length;

  return (
    <section id="compare" className={`scroll-mt-28 ${className}`} aria-labelledby="compare-heading" data-testid="home-comparison">
      <SectionHeading
        as="h2"
        id="compare-heading"
        align="left"
        eyebrow="Side by side"
        title="Compare the homes"
        lede="The facts that decide it, in one place."
        className="mb-8"
      />
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <table className="w-full border-collapse border border-sand-deep bg-shell font-sans text-[12.5px] md:text-[15px]">
          <thead>
            <tr>
              <th scope="col" className="w-[30%] border-b border-sand-deep bg-sand p-2.5 md:p-4 text-left align-bottom">
                <span className="sr-only">Feature</span>
              </th>
              {homes.map((home) => {
                const cover = coverOf(home);
                return (
                  <th key={home.id} scope="col" className="border-b border-l border-sand-deep bg-sand p-2.5 md:p-4 text-center align-bottom" style={{ width: `${70 / cols}%` }}>
                    <Link href={`/properties/${home.slug}`} className="group block">
                      {cover && (
                        <img
                          src={img(cover, 480)}
                          alt=""
                          className="mx-auto mb-2 md:mb-3 aspect-[3/2] w-full max-w-[130px] md:max-w-[220px] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <span className="block font-serif text-[15px] font-normal leading-tight text-ink md:text-xl">{home.title}</span>
                      <NewListingBadge slug={home.slug} className="mt-1" />
                      <span className="mt-1 hidden text-[11px] font-normal uppercase tracking-[0.2em] text-driftwood md:block">
                        {home.public_location_label || home.general_location}
                      </span>
                    </Link>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.label} className="odd:bg-shell even:bg-sand">
                <th scope="row" className="border-b border-sand-deep p-2.5 md:p-4 text-left font-normal leading-snug text-ink-soft">
                  {row.label}
                </th>
                {homes.map((home) => (
                  <td key={home.id} className="border-b border-l border-sand-deep p-2.5 md:p-4 text-center">
                    {row.cell(home)}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="p-2.5 md:p-4" />
              {homes.map((home) => (
                <td key={home.id} className="border-l border-sand-deep p-2.5 md:p-4 text-center">
                  <Link
                    href={`/properties/${home.slug}`}
                    className="inline-flex items-center justify-center bg-ink px-3 py-2.5 md:px-5 md:py-3 text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.12em] text-shell transition-colors hover:bg-ink/90"
                    data-testid={`compare-cta-${home.slug}`}
                  >
                    View {home.title}
                  </Link>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
